import { useContext, useEffect, useState } from 'react';
import useAppQuery from 'hooks/useAppQuery';
import CalendarContext from 'pages/stackedCalendar/CalendarContext';
import API from 'services/API';
import useAppUser from 'hooks/useAppUser';
import { Lead, LeadStatus } from 'models/Leads';
import {
  addDays,
  differenceInCalendarDays,
  endOfDay,
  format,
  setHours,
  startOfDay,
} from 'date-fns';
import { isBetween } from 'utils/dateTimeUtils';
import { PropertyBusinessType } from 'models/Properties';
import CalendarBodyContext from 'pages/calendar/body/CalendarBodyContext';
import StandaloneCalendarContext from 'pages/calendar/CalendarContext';
import {
  getLeadCheckInDate,
  getLeadCheckOutDate,
  isFromICS,
} from '../../utils/lead/leadUtils';
import useCalendarBodyRefreshRequest from '../stackedCalendar/useCalendarBodyRefreshRequest';
import { getUnloadedDatesForLeads } from './Calendar.utils';
import { StandaloneCalendarDataFetchParams } from './Calendar.types';

const useLeadsData = () => {
  const { daysCalendar, setDaysCalendar } = useContext(CalendarBodyContext);
  const { calendarFilter } = useContext(StandaloneCalendarContext);
  const { leadsMap, selectedProperty, propertiesMap, daysLeadsMap } =
    useContext(CalendarContext);
  const bodyRefreshRequest = useCalendarBodyRefreshRequest();
  const { agencyUid, isOwner } = useAppUser();
  const [fetchParams, setFetchParams] =
    useState<StandaloneCalendarDataFetchParams>();

  const { data: leads, isLoading: isLeadLoading } = useAppQuery(
    ['getLeads', selectedProperty, agencyUid, fetchParams],
    async () => {
      // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      const { start, end } = fetchParams;

      const bookingValueField = isOwner
        ? 'ownerCommission{amount,currency}'
        : 'order{currency,totalAmount}';

      // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      const property = propertiesMap[selectedProperty];
      const isUnit = property.businessType === PropertyBusinessType.UNIT;
      const propertyToQuery = isUnit ? property.unitTypeUid : selectedProperty;

      if (start && end) {
        const graphqlQuery = (leadsUids?: string[]) => {
          const hasLeadsUids = Array.isArray(leadsUids) && leadsUids.length > 0;

          return {
            operationName: '',
            query: `{leads (
              ${hasLeadsUids ? `uids:["${leadsUids.join('","')}"],` : ''}
              agencyUid: "${agencyUid}",
              ${hasLeadsUids ? '' : `propertyUids: ["${propertyToQuery}"],`}
              checkin_to: "${format(end, 'yyyy-MM-dd')}",
              checkout_from: "${format(start, 'yyyy-MM-dd')}",
              statuses: [BLOCKED,PENDING,ON_HOLD,BOOKED_EXTERNALLY,BOOKED_BY_AGENT,BOOKED_BY_CUSTOMER,BOOKED_BY_OWNER,STAY,ARCHIVED,PAID_IN_FULL,SAMPLE]
              ,limit:"2000"
          ){
            uid,
            firstName,
            lastName,
            affiliate,
            email,
            source,
            checkInDateTimeString,
            checkOutDateTimeString,
            checkInLocalDateTime,
            checkOutLocalDateTime,
            childrenCount,
            adultCount,
            petCount,
            infantCount,
            status,
            ${bookingValueField},
            property{uid, mainPicture{tinyThumbnail}},
            unitUid,
            creator{type},
            hotelUid,
            groupUid,
            isGroupBooking
          }}`,
            variables: {},
          };
        };

        // Fetch leads with propertyUids filter and without leadsUids filters
        const response = await API.post<any>('/v3/graphql', graphqlQuery());

        const leadsData = response.data.data.leads;

        const groupBookings = leadsData
          // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          .filter((lead) => lead.groupUid)
          // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          .map((lead) => lead.groupUid)
          // remove duplicates
          // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          .filter((uid, index, self) => self.indexOf(uid) === index);

        if (groupBookings.length) {
          // Fetch leads with leadsUids filters and without propertyUids filter, because main bookings may belong to a different property than the one currently being viewed.
          const groupBookingsResponse = await API.post<any>(
            '/v3/graphql',
            graphqlQuery(groupBookings),
          );
          const groupBookingsData = groupBookingsResponse.data.data.leads;

          // Filter out null entries: the API may return null for UIDs it cannot resolve, which would otherwise cause runtime errors when processing the leads array.
          return leadsData.concat(groupBookingsData).filter(Boolean);
        }

        return leadsData;
      }
      return null;
    },
    { enabled: !!fetchParams },
  );

  useEffect(() => {
    if (!leads) return;
    // Reorder leads PMPK-4673
    leads
      .sort((a: Lead, b: Lead) => {
        if (isFromICS(a.source)) return 1;
        if (isFromICS(b.source)) return -1;
        return 0;
      })
      // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      .sort((a, b) => {
        return differenceInCalendarDays(a.checkIn, b.checkIn);
      });

    // Leads are loaded, let's prepare lead data in Calendar day
    // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    daysCalendar[selectedProperty]?.daysCalendar.map((d) => {
      // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      if (isBetween(d.date, calendarFilter.start, calendarFilter.end)) {
        d.leads = [];
      }

      return d;
    });

    for (let l = 0; l < leads.length; l += 1) {
      const lead = leads[l];

      if (!leadsMap[lead.uid]) {
        leadsMap[lead.uid] = lead;
      }

      // Set all leads to the same time, to avoid third party agency lead to have 00:00:00 PMPK-4610
      const checkIn = setHours(getLeadCheckInDate(lead), 11);
      const checkOut = setHours(getLeadCheckOutDate(lead), 3);

      if (
        (!lead.unitUid && lead.property.uid === selectedProperty) ||
        (lead.unitUid && lead.unitUid === selectedProperty)
      ) {
        for (
          let tmp = checkIn;
          differenceInCalendarDays(tmp, checkOut) <= 0;
          tmp = addDays(tmp, 1)
        ) {
          // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          daysCalendar[selectedProperty]?.daysCalendar.map((d) => {
            if (isBetween(tmp, startOfDay(d.date), endOfDay(d.date))) {
              // Prioritize booking on the display
              if (d.leads === null) {
                d.leads = [];
              }

              if (lead.status !== LeadStatus.BLOCKED) {
                d.leads.push(lead);
              } else {
                d.leads.unshift(lead);
              }

              if (daysLeadsMap.current[format(d.date, 'yyyy-MM-dd')]) {
                daysLeadsMap.current[format(d.date, 'yyyy-MM-dd')].push({
                  uid: lead.uid,
                  checkIn,
                  checkOut,
                });
              } else {
                daysLeadsMap.current[format(d.date, 'yyyy-MM-dd')] = [
                  {
                    uid: lead.uid,
                    checkIn,
                    checkOut,
                  },
                ];
              }
            }

            return d;
          });
        }
      }
    }
    setDaysCalendar((prevState) => ({
      ...prevState,
      // @ts-expect-error TS2464 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      [selectedProperty]: {
        // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        daysCalendar: daysCalendar[selectedProperty]?.daysCalendar,
      },
    }));
  }, [leads]);

  useEffect(() => {
    setFetchParams(
      // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      getUnloadedDatesForLeads(daysCalendar[selectedProperty]?.daysCalendar),
    );
    // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  }, [daysCalendar[selectedProperty]?.daysCalendar?.length]);

  useEffect(() => {
    if (bodyRefreshRequest) {
      const { properties } = bodyRefreshRequest;

      const unitTypeFromUnit =
        // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        propertiesMap[selectedProperty].unitTypeUid === properties[0];
      // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      if (!properties.includes(selectedProperty) && !unitTypeFromUnit) {
        return;
      }

      // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      setFetchParams({
        ...getUnloadedDatesForLeads(
          // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          daysCalendar[selectedProperty]?.daysCalendar,
        ),
        requestId: new Date().getTime(),
      });
    }
  }, [bodyRefreshRequest]);

  return {
    isLoading: isLeadLoading,
  };
};

export default useLeadsData;
